import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import MicroApp from '../components/micro-frontend/MicroApp';
import { useConfig } from '../config';
import { useLaunchDarkly } from '../launchdarkly';
import { OrganizationMigrationStatus } from '../models';
import { resolvePath, RouteConfig, sso, useQuery } from '../routes';
import {
  fromAccounts,
  fromOrganizations,
  fromSecuritySetting,
} from '../store';
import {
  accountSetting,
  accountSubscriptionManagement,
  accountUserManagement as accountUserManagementPage,
  activateLicenseServer,
  dashboard,
  licensesAllocation,
  licenseUtilization,
  maintenanceProcess,
  organizationManagement,
  orgCardView,
  paymentMethod,
  projectManagement,
  securitySettings,
  supportManagement,
  testCloudDashboard,
  userGroup,
  userManagement,
} from './routes';

const SidebarOrgRemodeling = () => {
  const { flags } = useLaunchDarkly();
  const { config } = useConfig();

  let orgId = Number(useQuery().get('orgId'));
  const organization = useSelector(fromOrganizations.selectOrganizationById(orgId));
  const organizations = useSelector(fromOrganizations.selectAllOrganizations);

  let accountId = useQuery().get('accountId') ?? '';
  if (!accountId) {
    accountId = organization?.accountId?.toString() ?? '';
  }

  const account = useSelector(fromAccounts.selectAccountById(Number(accountId)));

  const securitySetting = useSelector(
    fromSecuritySetting.selectAllSecuritySettingsByAccountId(Number(accountId)),
  );

  const orgMigrationStatus = useSelector(
    fromOrganizations.selectOrgMigrationStatusById(Number(orgId)),
  );

  if (config?.onpremise && !orgId) {
    orgId = organizations[0]?.id;
  }

  const resolveOrgHomePath = (orgId: number) => {
    let projectUri: string;
    const oldUI = resolvePath(sso, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/teams/projects` });
    const newUI = resolvePath(projectManagement, undefined, { orgId: `${orgId}` });
    const maintenanceUI = resolvePath(maintenanceProcess, undefined);
    if (orgMigrationStatus === OrganizationMigrationStatus.MIGRATED
      || !!config?.onpremise) {
      projectUri = newUI;
    } else if (flags?.projectStandardizeEnabled) {
      projectUri = maintenanceUI;
    } else if (flags?.projectManagementNewUIEnabled) {
      projectUri = newUI;
    } else {
      projectUri = oldUI;
    }
    return projectUri;
  };

  const goToProjects = (orgId: number) => {
    let projectUri: string;
    const oldUI = resolvePath(sso, undefined, { redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/teams/projects` });
    const newUI = resolvePath(projectManagement, undefined, { orgId });
    const maintenanceUI = resolvePath(maintenanceProcess, undefined);
    if (orgMigrationStatus === OrganizationMigrationStatus.MIGRATED
      || !!config?.onpremise) {
      projectUri = newUI;
    } else if (flags?.projectStandardizeEnabled) {
      projectUri = maintenanceUI;
    } else if (flags?.projectManagementNewUIEnabled) {
      projectUri = newUI;
    } else {
      projectUri = oldUI;
    }
    return projectUri;
  };

  const resolveProjectManagementPath = (orgId: number) => buildLink(
    goToProjects(orgId),
  );

  const resolvedTeamsPath = (orgId: number) => {
    if (flags?.projectStandardizeEnabled) {
      return resolvePath(maintenanceProcess, undefined);
    }
    return resolvePath(sso, undefined, {
      redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/teams/settings`,
    });
  };

  const resolveOrgSettingPath = (orgId: number) => buildLink(
    resolvePath(organizationManagement, undefined, { orgId, accountId }),
  );

  const resolveLicenseManagementPath = (orgId: number) => buildLink(
    resolvePath(sso, undefined, {
      redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/organization/${orgId}/admin/license_keys/kse`,
    }),
  );

  const resolveASPath = (accountId: number, accountSetting: RouteConfig) => buildLink(
    resolvePath(accountSetting, undefined, { accountId }),
  );

  const resolveAUMPath = (accountId: number, accountUMConfig: RouteConfig) => buildLink(
    resolvePath(accountUMConfig, { tab: 'active' }, { accountId }),
  );

  const buildLink = (defaultG2Link: string) => {
    if (isEmpty(securitySetting) || isEmpty(account)) return '';

    return defaultG2Link;
  };

  const fullPathWithQuery = window.location.pathname + window.location.search;

  const accountSettingUrl = resolvePath(accountSetting, undefined, { accountId });
  const orgSettingManagement = resolvePath(organizationManagement, undefined, { orgId, accountId });

  // Create a Map to store the key-value pairs
  const mapLink = new Map();
  mapLink.set(accountSettingUrl, 'admin/account/general');
  mapLink.set(orgSettingManagement, `admin/organization/organization-management?organizationId=${orgId}`);

  // Convert Map entries to an array and iterate over it
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Array.from(mapLink.entries()).forEach(([key, _]) => {
    if ((key.includes(fullPathWithQuery) || fullPathWithQuery.startsWith(key))
      && fullPathWithQuery !== buildLink(fullPathWithQuery)) {
      window.location.assign(buildLink(key));
    }
  });

  return (
    <MicroApp
      id="katalon-navigation-ui"
      nodeId="katalon-navigation-ui-sidebar"
      path={process.env.REACT_APP_NAVIGATION_MICROAPP_URL ?? ''}
      overrideConfig={{
        myBaseUrl: `${window.location.origin}${process.env.PUBLIC_URL}`,
        katOneBaseName: config?.onpremise ? process.env.PUBLIC_URL : undefined,
        customDomainPostfix: `${process.env.REACT_APP_CUSTOM_DOMAIN_POSTFIX}`,
        migrationStatus: orgMigrationStatus,
        navigationPaths: {
          home: resolvePath(orgCardView, undefined, { accountId }),
          organizationsManagement: resolvePath(orgCardView, undefined, { accountId }),
          subscriptionManagement: resolvePath(
            accountSubscriptionManagement,
            undefined,
            { accountId },
          ),
          paymentMethodManagement: resolvePath(paymentMethod, undefined, {
            accountId,
          }),
          securityManagement: resolvePath(securitySettings, undefined, {
            accountId,
          }),
          accountSetting: resolveASPath(Number(accountId), accountSetting),
          licenseAllocation: resolvePath(
            licensesAllocation,
            { page: 'kse' },
            { accountId },
          ),
          accountUserManagement: resolveAUMPath(Number(accountId), accountUserManagementPage),
          projectManagement: (orgId: number) => resolveProjectManagementPath(orgId),
          userManagement: (orgId: number) => resolvePath(userManagement, undefined, { orgId }),
          licenseManagement: resolveLicenseManagementPath,
          kseDashboard: (orgId: number) => resolvePath(licenseUtilization, undefined, { orgId }),
          platformDashboard: (orgId: number) => resolvePath(dashboard, undefined, { orgId }),
          testcloudDashboard: (orgId: number) => resolvePath(
            testCloudDashboard,
            undefined,
            { orgId },
          ),
          supportManagement: (orgId: number) => resolvePath(
            supportManagement,
            undefined,
            { orgId },
          ),
          teamsManagement: resolvedTeamsPath,
          organizationSetting: (orgId: number) => resolveOrgSettingPath(orgId),
          userGroup: (orgId: number) => resolvePath(userGroup, undefined, { orgId }),
          activateLicenseServer: resolvePath(activateLicenseServer, undefined, {
            accountId,
          }),
          emailConfiguration: resolvePath(sso, undefined, {
            redirect_uri: `${process.env.REACT_APP_ADMIN_URL}/email-server-configuration?accountId=${accountId}`,
          }),
        },
        resolveOrgHomePath,
        navigationType: 'SIDEBAR',
        nodeId: 'katalon-navigation-ui-sidebar',
      }}
    />
  );
};

export default SidebarOrgRemodeling;
